import BarSavedAt from 'components/BarSavedAt';
import getFullTextFromEditorState from 'components/BasicEditor/lib/get-full-text-from-editor-state';
import Timer from 'components/Timer';
import {LineWordCounter} from 'letrus-ui';
import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {
  getErrorAt,
  getIsLoadingSaveComposition,
} from 'store/reducers/compositions';
import {
  fetchServerStatusRequest,
  getCurrentTime,
} from 'store/reducers/globalConfiguration';
import {ApplicationState} from 'store/rootReducer';
import countTextEditorLines from 'utils/functions/count-text-editor-lines';
import countWords from 'utils/functions/count-words';
import {getHumanSavedAt} from 'utils/functions/getHumanSavedAt';
import stringToDraftState from 'utils/functions/stringToDraftState';
import {CompositionDetailExtended} from 'utils/types/compositions';
import getFeatureFlag from 'utils/functions/getFeatureFlags';
import {UserComposition} from '..';
import CompositionAlert from '../CompositionAlert';
import styles from './CompositionInfo.module.scss';

interface DispatchProps {
  fetchServerStatusRequest: typeof fetchServerStatusRequest;
}

interface StateProps {
  compositionRequestError: boolean;
  isLoadingSaveComposition: boolean;
  currentTime: string;
}

interface OwnProps {
  composition: ImmutableMap<CompositionDetailExtended>;
  userComposition?: UserComposition;
  onRestoreClick(): void;
  onManualSaveClick(): void;
  onTimesUpCallback(): void;
}

export type CompositionInfoProps = OwnProps & StateProps & DispatchProps;

function CompositionInfo({
  composition,
  currentTime,
  onRestoreClick,
  userComposition,
  onTimesUpCallback,
  onManualSaveClick,
  compositionRequestError,
  isLoadingSaveComposition,
  fetchServerStatusRequest,
}: CompositionInfoProps) {
  const [finishedTimer, setFinishedTimer] = useState(false);
  const wordsNumber = countWords(userComposition?.text ?? '');
  const testTimer = composition.getIn(['test', 'timer']);
  const isCompositionFinished =
    composition.get('flag_finished') || !!userComposition?.finished;

  const isReviewGridMultigenre =
    composition.getIn(['instruction_id', 'genre', 'review_grid']) === 61;

  const shouldRenderOverloadedReviewQueueWarning = getFeatureFlag(
    'overloaded_review_queue_warning',
  ).isOn;

  useEffect(() => {
    fetchServerStatusRequest();
  }, []);

  const textLinesCount = countTextEditorLines(
    getFullTextFromEditorState(stringToDraftState(userComposition?.text)),
  );

  return (
    <article className={styles.wrapper}>
      <div className={styles.alertArea}>
        <CompositionAlert
          trigger={textLinesCount >= 30 && !isReviewGridMultigenre}
          title={`Sua redação atingiu 30 linhas, o tamanho máximo permitido ${
            composition.getIn(['genre', 'name']) === 'ENEM'
              ? 'pelo Enem'
              : 'pela Fuvest'
          }`}
          text="Na Letrus, você até pode continuar escrevendo, mas não se esqueça de que, no exame oficial, você não terá essa oportunidade. Então, para praticar, recomendamos que mantenha seu texto sempre dentro do padrão!"
        />

        <CompositionAlert
          trigger={(userComposition?.title?.length ?? 0) >= 199}
          title="O título da sua redação atingiu o limite máximo de caracteres."
          text="Este campo deve ser utilizado apenas para o título da sua atividade. Tem certeza que está digitando no local correto?"
        />

        <CompositionAlert
          trigger={shouldRenderOverloadedReviewQueueWarning}
          text="Nossas filas de correção estão lotadas. Isso pode causar uma lentidão maior na entrega das suas notas"
        />
      </div>

      <div className={styles.wordCounter}>
        <LineWordCounter
          amountOfWords={wordsNumber}
          amountOfLines={textLinesCount}
          sufficientAmountOfWords={
            wordsNumber <
            (composition.getIn(['instruction_id', 'genre', 'min_words']) || 0)
              ? false
              : undefined
          }
        />
      </div>

      <div className={styles.savedAt}>
        <BarSavedAt
          userSavedAt={getHumanSavedAt(
            !!composition.get('finished'),
            userComposition?.modified,
            composition,
            compositionRequestError,
            isLoadingSaveComposition,
          )}
          onClickRestore={onRestoreClick}
          onClickSave={onManualSaveClick}
          hideChangeButtons={isCompositionFinished}
        />
      </div>

      {composition.get('test') && (
        <div className={styles.timer}>
          <Timer
            finishedTimer={finishedTimer}
            onFinishTimer={onTimesUpCallback}
            finishByHuman={false}
            updateFinishedTimer={() => setFinishedTimer(true)}
            endingTime={composition.getIn(['test', 'end_datetime'])}
            startingTime={composition.get('created')}
            currentTime={currentTime}
            maxDurationInHours={testTimer || 0}
          />
        </div>
      )}
    </article>
  );
}

export default connect(
  (state: ApplicationState) => ({
    compositionRequestError: getErrorAt(state),
    isLoadingSaveComposition: getIsLoadingSaveComposition(state),
    currentTime: getCurrentTime(state),
  }),
  {fetchServerStatusRequest},
)(CompositionInfo);
